import { type ReactNode } from 'react';

import { PanelTextSection } from 'common/components/Panel';
import { useTranslation } from 'common/hooks/useTranslation';
import { useIsEditablePayable } from 'modules/payable/hooks';
import { DroppablePanel } from 'src/core/common/components/Panel/DroppablePanel';
import { TripSection } from 'src/core/common/components/TripSection/TripSection';
import { useUser } from 'src/core/modules/app/hooks/useUser';
import { AnalyticEventName, track } from 'src/core/utils/analytics';

import { MileageSection } from './MileageSection';
import { PayableDescriptionSection } from './PayableDescriptionSection';
import style from './PayablePanel.module.css';
import { PayablePanelHeader } from './PayablePanelHeader';
import {
  ReceiptSection,
  useCanUploadDocumentaryEvidence,
} from './ReceiptSection';
import { PayableAccountingSectionContainer } from '../../containers/PayableAccountingSectionContainer';
import { PayableBeneficiariesSection } from '../../containers/PayableBeneficiariesSection/PayableBeneficiariesSection';
import { PayableDetailsSectionContainer } from '../../containers/PayableDetailsSectionContainer';
import { PayableHistoryDownloadContainer } from '../../containers/PayableHistoryDownloadContainer';
import type { Document as DocumentViewable } from '../../containers/PayablePanelContainer/documentViewer';
import type { Payable } from '../../containers/PayablePanelContainer/hooks/usePayableQuery/payable';
import { type RawPayableType } from '../../containers/PayablePanelContainer/hooks/usePayableQuery/query';
import { useIsUkMileagePayable, type PayableType } from '../../models/payable';

type Props = {
  payable: Payable;
  exportError?: ReactNode;
  panelActions?: ReactNode;
  onDocumentClick(document: DocumentViewable): void;
  onDocumentUpload(files: File[] | null): void;
  isUploadingDocument?: boolean;
  onClose(): void;
  className?: string;
};

export const FOOTER_ID = 'payable-panel-footer';

export const PayablePanel = ({
  payable,
  exportError,
  panelActions,
  onDocumentClick,
  onDocumentUpload,
  isUploadingDocument,
  onClose,
  className,
}: Props) => {
  const { t } = useTranslation('global');

  const transactionDescription = getTransactionDescription(payable.allocations);
  const isMileagePayable = getIsMileagePayable(payable.type);
  const isUkMileagePayable = useIsUkMileagePayable(payable);
  const isEditablePayable = useIsEditablePayable(payable);
  const canUploadDocumentaryEvidence = useCanUploadDocumentaryEvidence(payable);
  const user = useUser();

  return (
    <DroppablePanel
      className={className}
      title={t('payables.panel.title')}
      header={
        <PayablePanelHeader
          payable={payable}
          panelActions={panelActions}
          exportError={exportError}
        />
      }
      footer={<div className={style.PayablePanelFooter} id={FOOTER_ID} />}
      onClose={onClose}
      onUploadFiles={onDocumentUpload}
      canUploadFiles={canUploadDocumentaryEvidence}
    >
      <PayableDescriptionSection
        payable={payable}
        isEditable={isEditablePayable}
      />

      {transactionDescription && (
        <PanelTextSection
          isEmphasized
          title={t('payables.panel.transactionDescription')}
        >
          {transactionDescription}
        </PanelTextSection>
      )}
      <MileageSection payable={payable} />
      {(!isMileagePayable || isUkMileagePayable) && (
        <ReceiptSection
          payable={payable}
          onDocumentClick={(document) => {
            track(AnalyticEventName.BOOKKEEP_PAYABLE_ALL_RECEIPT_VIEWED, {
              payableId: payable.id,
            });
            onDocumentClick(document);
          }}
          onDocumentUpload={onDocumentUpload}
          isUploadingDocument={isUploadingDocument}
        />
      )}
      <PayableDetailsSectionContainer payable={payable} />
      <PayableAccountingSectionContainer payable={payable} />
      <TripSection
        entityId={payable.id}
        entityType="payables"
        isEditable={false} // we want readonly for now
        impersonatedUserId={
          payable.member?.id !== user.id ? payable.member?.id : undefined
        }
      />
      <PayableBeneficiariesSection payable={payable} withTitle />
      <PayableHistoryDownloadContainer payable={payable} />
    </DroppablePanel>
  );
};

const getTransactionDescription = (allocations: Payable['allocations']) => {
  const cardPaymentAllocation = allocations.find(
    (allocation) =>
      allocation.settlement?.kind === 'payment' &&
      allocation.settlement?.transaction.medium === 'card',
  );
  // only display description of card payment transaction
  if (cardPaymentAllocation) {
    return cardPaymentAllocation.settlement?.transaction.description;
  }
};

const getIsMileagePayable = (type: PayableType) => {
  return type === 'mileageAllowance';
};

export const reshapePayableType = (rawType: PayableType): RawPayableType => {
  switch (rawType) {
    case 'cardPurchase':
      return 'card_purchase';
    case 'invoicePurchase':
      return 'invoice_purchase';
    case 'claimedBill':
      return 'expense_claim';
    case 'mileageAllowance':
      return 'mileage_allowance';
    case 'perDiemAllowance':
      return 'per_diem_allowance';
    case 'reversal':
      return 'reversal';
    default:
      throw new Error(`Unknown payable type ${rawType}`);
  }
};
